/**
 * Hook: Premium-Support-Verfügbarkeit
 *
 * Lädt den Verfügbarkeits-Status vom Backend, aktualisiert sich:
 *   - Initial direkt nach Mount
 *   - Alle 60 Sekunden (Polling)
 *   - Beim Browser-Tab-Focus (User schaut wieder hin → soll aktuell sein)
 *
 * Verhalten:
 *   - Während des allerersten Loads: data ist null, isLoading true
 *   - Bei Re-Fetches (Polling/Focus): Daten bleiben stehen, isLoading false —
 *     verhindert Flackern, da Daten nur minutenweise relevant ändern
 *   - Bei Fehler: error gesetzt, data bleibt vom letzten erfolgreichen Fetch
 *     (Stale-while-revalidate)
 *
 * Performance:
 *   - Backend cached 60s, Browser cached 15s. Selbst bei mehreren Tabs:
 *     der zweite Tab trifft den Cache, kein DB-Load.
 *   - `enabled`-Parameter erlaubt es, den Hook zu deaktivieren wenn Bubble/Panel
 *     gar nicht angezeigt werden (User ohne Chat-Zugang).
 */

import { useEffect, useRef, useState } from 'react';
import { fetchSupportAvailability, type AvailabilityState } from '../services/support/availabilityService';

interface UseSupportAvailabilityOptions {
  /** Wenn false, wird gar nicht gefetcht. Default: true. */
  enabled?: boolean;
  /** Polling-Intervall in ms. Default: 60_000 (1 min). */
  intervalMs?: number;
}

interface UseSupportAvailabilityResult {
  data: AvailabilityState | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const DEFAULT_INTERVAL_MS = 60_000;

export function useSupportAvailability(
  options: UseSupportAvailabilityOptions = {}
): UseSupportAvailabilityResult {
  const enabled = options.enabled !== false;
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;

  const [data, setData] = useState<AvailabilityState | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  // Stabile Referenz auf den Fetch, damit useEffect-Dependencies sauber sind
  const fetchRef = useRef<() => Promise<void>>(async () => {});

  fetchRef.current = async () => {
    if (!enabled) return;
    try {
      const next = await fetchSupportAvailability();
      setData(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Fehler beim Laden der Verfügbarkeit'));
      // data bewusst NICHT zurücksetzen → Stale-while-revalidate
    } finally {
      setIsLoading(false);
    }
  };

  // Initial-Load + Polling-Setup
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    // Initial sofort fetchen
    void fetchRef.current();

    // Polling
    const intervalId = setInterval(() => {
      void fetchRef.current();
    }, intervalMs);

    // Re-fetch beim Tab-Focus (visibility-Change)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);

  return {
    data,
    isLoading,
    error,
    refetch: () => fetchRef.current()
  };
}
